///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Screen-space companion to the earthquake cutout block. While a cutout
/// is open it replaces the anchored info card (which would sit on top of
/// the block) with a column docked to the left edge: headline the instant
/// the toggle is pressed, then rows that fill in as the sequence, terrain,
/// history and USGS products land. Spatial facts stay in the 3D block;
/// everything verbal and statistical lives here, where it can wrap, be
/// read at any camera angle, and be hovered.
///
/// Phones get the same card as a bottom sheet, which would otherwise sit
/// on top of the very block it describes. So the sheet has three heights
/// the user moves between with the handle (tap, swipe) or the header
/// chevron without closing the cutout or moving focus off the block:
/// peek (headline only, the default), expanded (the full column), and
/// minimized (a one-line bar). Whatever it covers is published as the
/// engine's bottom viewport inset so the block rises above the sheet.
///
/// Hiding the panel never closes the block: the header X minimizes the
/// phone sheet to its bar and collapses the desktop column to a chip, with
/// the quake still focused and the block still up. Only the X on that bar
/// or chip closes the cross-section and returns to the quake.
///

import { Badge, formatTimeAgo } from "@/components/controls/anchored-panel/panelAtoms"
import { useIsMobile } from "@/components/utils/use-mobile"
import { formatDepth } from "@/earthbrowser/utils/unitFormatters"
import { cLayersPanelEdgeInset, cLayersPanelWidth, useChromeInsets } from "@/hooks/useBottomChromeOffset"
import { setCutoutHudCollapsed, setMobileInfoSheetOpen, setQuakeCutout, setQuakeCutoutSheetHeight, type QuakeCutoutChart, type QuakeCutoutState } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { ChevronDown, ChevronUp, Cuboid, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState, type ReactNode, type TouchEvent } from "react"
import { CutoutHudChip } from "./CutoutHudChip"
import { blockSentence, depthColor, exposureSentences, forecastSentence, geometrySentence, historySentences, sequenceSentence, slabSentence, statisticsSentences, tsunamiSentence } from "./cutoutText"

const cQuakeKindStyle = { color: "#fca5a5" }
const cQuakeBorderStyle = { borderColor: "rgba(248, 113, 113, 0.2)" }

/** Column width; also published as the engine's left viewport inset (useViewportInsetsPublisher) */
export const cCutoutHudWidth = cLayersPanelWidth
/** Clearance under the top-left Layers / Search buttons */
const cCutoutHudTop = 64

/** Phone sheet heights (viewport fraction); peek fits the headline, expanded scrolls the rows */
const cSheetPeekMaxVh = 30
const cSheetExpandedMaxVh = 60
/** Vertical travel on the handle that counts as a swipe rather than a tap */
const cSheetSwipeThresholdPx = 24

type SheetMode = "minimized" | "peek" | "expanded"

export function QuakeCutoutHud() {
	const cutout = useAppSelector(state => state.dialog.quakeCutout)
	const isMobile = useIsMobile()
	const dispatch = useAppDispatch()
	const chromeLeft = useChromeInsets().left

	// Phones: the time control shares the bottom edge; step aside like the info sheets do
	const showing = cutout !== null
	useEffect(() => {
		if (!isMobile || !showing) return
		dispatch(setMobileInfoSheetOpen(true))
		return () => {
			dispatch(setMobileInfoSheetOpen(false))
		}
	}, [isMobile, showing, dispatch])

	if (!cutout) return null
	const close = () => dispatch(setQuakeCutout(null))

	if (isMobile) {
		return <MobileSheet cutout={cutout} onClose={close} />
	}

	if (cutout.hudCollapsed) {
		const q = cutout.quake
		return (
			<CutoutHudChip
				kind="Cross-section"
				kindStyle={cQuakeKindStyle}
				borderStyle={cQuakeBorderStyle}
				expandLabel="Show cross-section details"
				closeLabel="Close cross-section"
				closeTitle="Close the cross-section and return to the quake"
				onExpand={() => dispatch(setCutoutHudCollapsed({ cutout: "quake", collapsed: false }))}
				onClose={close}
			>
				{q ? <><span className="font-semibold text-white">M {q.mag.toFixed(1)}</span> {q.place}</> : "Loading…"}
			</CutoutHudChip>
		)
	}

	return (
		<div
			className="fixed z-40 pointer-events-none flex flex-col"
			style={{
				left: chromeLeft + cLayersPanelEdgeInset,
				top: cCutoutHudTop,
				width: cCutoutHudWidth,
				// Stop above the legend tray chips in the bottom-left corner
				maxHeight: `calc(100vh - ${cCutoutHudTop}px - 120px)`
			}}
		>
			<div
				className={cn(
					"pointer-events-auto flex flex-col bg-black/65 backdrop-blur-xl border border-red-400/20 rounded-xl shadow-2xl overflow-hidden",
					"motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200 motion-safe:slide-in-from-left-4"
				)}
			>
				<HudHeader onHide={() => dispatch(setCutoutHudCollapsed({ cutout: "quake", collapsed: true }))} />
				<div className="flex-1 min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] px-4 pb-4 space-y-3">
					<Headline cutout={cutout} />
					<Rows cutout={cutout} />
				</div>
			</div>
		</div>
	)
}

/**
 * Bottom sheet with the three heights. Publishes the canvas height it
 * covers so the engine's lens shift keeps the block in the visible part
 * of the globe; every mode change glides the block with it
 */
function MobileSheet({ cutout, onClose }: { cutout: QuakeCutoutState; onClose: () => void }) {
	const dispatch = useAppDispatch()
	const [mode, setMode] = useState<SheetMode>("peek")
	const swipeStartY = useRef<number | null>(null)
	const observerRef = useRef<ResizeObserver | null>(null)

	// Back to peek for a different quake
	useEffect(() => {
		setMode("peek")
	}, [cutout.quakeId])

	// Covered height = viewport bottom to the sheet's top edge (bottom
	// anchored, so the edge inset counts). Measured on the untransformed
	// wrapper rather than the card, whose entrance slide would skew the
	// first reading
	const wrapperRef = useCallback((wrapper: HTMLDivElement | null) => {
		observerRef.current?.disconnect()
		observerRef.current = null
		if (!wrapper) return
		const publish = () => {
			dispatch(setQuakeCutoutSheetHeight(Math.max(0, Math.round(window.innerHeight - wrapper.getBoundingClientRect().top))))
		}
		publish()
		const observer = new ResizeObserver(publish)
		observer.observe(wrapper)
		observerRef.current = observer
	}, [dispatch])

	useEffect(() => () => {
		observerRef.current?.disconnect()
		observerRef.current = null
		dispatch(setQuakeCutoutSheetHeight(0))
	}, [dispatch])

	const expanded = mode === "expanded"
	const grow = () => setMode(m => (m === "minimized" ? "peek" : "expanded"))
	const shrink = () => setMode(m => (m === "expanded" ? "peek" : "minimized"))

	const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
		swipeStartY.current = event.touches[0]?.clientY ?? null
	}
	// Swipe up grows a step, swipe down shrinks one; short travel is a tap (onClick)
	const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
		const startY = swipeStartY.current
		swipeStartY.current = null
		const endY = event.changedTouches[0]?.clientY
		if (startY === null || endY === undefined) return
		const travel = endY - startY
		if (travel < -cSheetSwipeThresholdPx) {
			event.preventDefault()
			grow()
		} else if (travel > cSheetSwipeThresholdPx) {
			event.preventDefault()
			shrink()
		}
	}
	const handleProps = {
		onTouchStart: handleTouchStart,
		onTouchEnd: handleTouchEnd
	}

	const q = cutout.quake

	return (
		<div ref={wrapperRef} className="fixed inset-x-2 z-40 pointer-events-none" style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)" }}>
			<div
				className={cn(
					"pointer-events-auto flex flex-col bg-black/65 backdrop-blur-xl border border-red-400/20 rounded-xl shadow-2xl overflow-hidden",
					"transition-[max-height] duration-300 ease-out",
					"motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200 motion-safe:slide-in-from-bottom-4"
				)}
				style={{ maxHeight: mode === "minimized" ? undefined : `${expanded ? cSheetExpandedMaxVh : cSheetPeekMaxVh}vh` }}
			>
				{mode === "minimized" ? (
					// One line: identity plus the two ways out (restore, close)
					<div
						className="flex items-center gap-2 pl-3 pr-2 py-2 min-h-11"
						role="button"
						tabIndex={0}
						aria-label="Show cross-section details"
						onClick={grow}
						onKeyDown={event => {
							// The close button inside handles its own keys
							if (event.target !== event.currentTarget) return
							if (event.key === "Enter" || event.key === " ") {
								event.preventDefault()
								grow()
							}
						}}
						{...handleProps}
					>
						<Cuboid className="w-3.5 h-3.5 shrink-0 text-red-300" />
						<div className="flex-1 min-w-0 flex items-baseline gap-2 text-xs">
							{q ? (
								<>
									<span className="font-semibold text-white">M {q.mag.toFixed(1)}</span>
									<span className="truncate text-slate-300">{q.place}</span>
								</>
							) : (
								<span className="text-slate-300">Cross-section</span>
							)}
						</div>
						<ChevronUp className="w-4 h-4 shrink-0 text-slate-400" aria-hidden />
						<CloseButton onClose={onClose} />
					</div>
				) : (
					<>
						<div
							role="button"
							tabIndex={0}
							aria-label={expanded ? "Collapse cross-section details" : "Expand cross-section details"}
							aria-expanded={expanded}
							className="shrink-0 flex justify-center pt-2 pb-1 touch-none"
							onClick={() => setMode(expanded ? "peek" : "expanded")}
							onKeyDown={event => {
								if (event.key === "Enter" || event.key === " ") {
									event.preventDefault()
									setMode(expanded ? "peek" : "expanded")
								}
							}}
							{...handleProps}
						>
							<div className="h-1 w-10 rounded-full bg-white/30" />
						</div>
						<HudHeader onHide={() => setMode("minimized")} compact>
							{!expanded && (
								<HeaderIconButton label="Expand cross-section details" onClick={grow}>
									<ChevronUp className="w-4 h-4" />
								</HeaderIconButton>
							)}
							<HeaderIconButton label={expanded ? "Collapse cross-section details" : "Minimize cross-section panel"} onClick={shrink}>
								<ChevronDown className="w-4 h-4" />
							</HeaderIconButton>
						</HudHeader>
						<div className="flex-1 min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] px-4 pb-4 space-y-3">
							<Headline cutout={cutout} />
							{expanded && <Rows cutout={cutout} />}
						</div>
					</>
				)}
			</div>
		</div>
	)
}

/** Panel header; its X hides the panel (chip on desktop, bar on phones) and leaves the block open */
function HudHeader({ onHide, compact = false, children }: { onHide: () => void; compact?: boolean; children?: ReactNode }) {
	return (
		<div className={cn("shrink-0 flex items-center justify-between px-4 pb-1", compact ? "pt-1" : "pt-3")}>
			<div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-red-300">
				<Cuboid className="w-3.5 h-3.5" />
				Cross-section
			</div>
			<div className="flex items-center gap-1">
				{children}
				<HeaderIconButton label="Hide cross-section details" title="Hide this panel; the block stays" onClick={onHide}>
					<X className="w-3.5 h-3.5" />
				</HeaderIconButton>
			</div>
		</div>
	)
}

function CloseButton({ onClose }: { onClose: () => void }) {
	return (
		<HeaderIconButton label="Close cross-section" title="Close the cross-section and return to the quake" onClick={onClose}>
			<X className="w-3.5 h-3.5" />
		</HeaderIconButton>
	)
}

function HeaderIconButton({ label, title, onClick, children }: { label: string; title?: string; onClick: () => void; children: ReactNode }) {
	return (
		<button
			onClick={event => {
				// Sits inside the sheet's tappable handle/bar rows
				event.stopPropagation()
				onClick()
			}}
			className="pointer-events-auto w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
			aria-label={label}
			title={title}
		>
			{children}
		</button>
	)
}

function Headline({ cutout }: { cutout: QuakeCutoutState }) {
	const q = cutout.quake
	if (!q) {
		return <Skeleton lines={2} wide />
	}
	const eventDate = new Date(q.eventTime)
	return (
		<div className="space-y-1">
			<div className="flex items-center gap-2">
				<span className="text-3xl font-bold text-white leading-none">M {q.mag.toFixed(1)}</span>
				{q.alert && <Badge colorClass={alertColor(q.alert)}>{q.alert}</Badge>}
				{q.tsunami && <Badge colorClass="bg-blue-500/20 text-blue-300 border border-blue-400/30">Tsunami</Badge>}
				{cutout.role === "member" && (
					<Badge colorClass="bg-slate-500/20 text-slate-300 border border-slate-400/30">in a swarm</Badge>
				)}
			</div>
			<div className="text-sm text-slate-200 leading-snug">{q.place}</div>
			<div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-400">
				<span>{formatTimeAgo(eventDate)}</span>
				<span className="text-slate-600">&bull;</span>
				<span>{eventDate.toLocaleString()}</span>
				<span className="text-slate-600">&bull;</span>
				<span>{formatDepth(q.depthKm)} deep</span>
			</div>
			{cutout.status === "error" && (
				<div className="text-xs text-red-300/80">Couldn't build the cross-section for this quake.</div>
			)}
		</div>
	)
}

function Rows({ cutout }: { cutout: QuakeCutoutState }) {
	const s = cutout.summary
	const stages = cutout.stages
	const analysis = cutout.role === "mainshock" ? s?.analysis : undefined
	const history = s?.history

	return (
		<>
			<Row label="Sequence" ready={stages.sequence}>
				{sequenceSentence(cutout)}
			</Row>
			<Row label="Block" ready={stages.sequence}>
				{s && blockSentence(s)}
				{s && !stages.terrain && <span className="text-slate-600"> · terrain loading…</span>}
			</Row>
			<Row label="History" ready={stages.history}>
				{history ? historySentences(history).map((line, i) => <div key={i}>{line}</div>) : "Historical catalog not available yet"}
			</Row>
			<Row label="Subduction interface" ready={stages.slab}>
				{s?.slab && cutout.quake ? slabSentence(s.slab, cutout.quake.depthKm) : "No Slab2 subduction interface under this block"}
			</Row>
			{analysis && (
				<>
					<Row label="Fault geometry" ready={stages.detail}>
						{geometrySentence(analysis) ?? "Too few aftershocks to fit a plane"}
					</Row>
					<Row label="Statistics" ready={stages.sequence}>
						{statisticsSentences(analysis, history?.regionalB).map((line, i) => <div key={i}>{line}</div>)}
						{statisticsSentences(analysis).length === 0 && "Too few aftershocks above the completeness magnitude"}
					</Row>
					<ForecastRow analysis={analysis} ready={stages.detail} />
					{(s?.exposure || !stages.exposure) && cutout.quake && cutout.quake.mag >= 5 && (
						<Row label="Shaking exposure" ready={stages.exposure}>
							{s?.exposure && exposureSentences(s.exposure, cutout.quake.mmi).map((line, i) => <div key={i}>{line}</div>)}
						</Row>
					)}
					{s?.tsunami && (
						<Row label="Tsunami travel time" ready={stages.terrain}>
							{tsunamiSentence(s.tsunami)}
						</Row>
					)}
					{cutout.chart && <SequenceChart chart={cutout.chart} />}
				</>
			)}
		</>
	)
}

function ForecastRow({ analysis, ready }: { analysis: NonNullable<QuakeCutoutState["summary"]>["analysis"] & object; ready: boolean }) {
	const line = forecastSentence(analysis.forecast)
	if (ready && !line) return null
	return (
		<Row label="USGS aftershock forecast" ready={ready}>
			{line}
		</Row>
	)
}

function Row({ label, ready, children }: { label: string; ready: boolean; children: ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-300/80">{label}</div>
			{ready ? (
				<div className="text-xs text-slate-300 leading-snug space-y-0.5">{children}</div>
			) : (
				<Skeleton lines={1} />
			)}
		</div>
	)
}

function Skeleton({ lines, wide = false }: { lines: number; wide?: boolean }) {
	return (
		<div className="space-y-1.5 motion-safe:animate-pulse" aria-hidden>
			{Array.from({ length: lines }, (_, i) => (
				<div key={i} className={cn("h-3 rounded bg-white/10", i === 0 && wide ? "w-2/3" : "w-full")} />
			))}
		</div>
	)
}

// ---------------------------------------------------------------------------
// Magnitude vs. log time since the mainshock
// ---------------------------------------------------------------------------

const cChartWidth = 288
const cChartHeight = 132
const cChartLeft = 26
const cChartRight = 8
const cChartTop = 8
const cChartBottom = 18
const cMinHours = 0.1
const cTimeTicks: Array<{ hours: number; label: string }> = [
	{ hours: 1, label: "1h" },
	{ hours: 6, label: "6h" },
	{ hours: 24, label: "1d" },
	{ hours: 24 * 7, label: "1w" },
	{ hours: 24 * 30, label: "30d" },
	{ hours: 24 * 365, label: "1y" }
]

/**
 * Log time turns Omori decay into a thinning trickle instead of a spike.
 * Points follow the scene clock like the hypocenters: scrubbing back hides
 * what hasn't happened yet; a cursor marks the clock
 */
function SequenceChart({ chart }: { chart: QuakeCutoutChart }) {
	const clockMs = useAppSelector(state => state.earthbrowser.clockCurrentTime)
	const hoursNow = (clockMs - chart.mainshockTimeMs) / 3.6e6
	// The axis always reaches the clock so the cursor stays on the chart
	const logMin = Math.log10(cMinHours)
	const logMax = Math.log10(Math.max(chart.maxHours, hoursNow, 24) * 1.15)
	const plotW = cChartWidth - cChartLeft - cChartRight
	const plotH = cChartHeight - cChartTop - cChartBottom
	const x = (hours: number): number => cChartLeft + plotW * (Math.log10(Math.max(hours, cMinHours)) - logMin) / (logMax - logMin)
	const y = (mag: number): number => cChartTop + plotH * (1 - (mag - chart.magMin) / Math.max(chart.magMax - chart.magMin, 0.5))

	const magTicks: number[] = []
	for (let m = chart.magMin; m <= chart.magMax; m++) magTicks.push(m)
	const timeTicks = cTimeTicks.filter(t => Math.log10(t.hours) <= logMax)

	return (
		<div className="space-y-1">
			<div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-300/80">Magnitude vs time (log)</div>
			<svg width={cChartWidth} height={cChartHeight} className="block" role="img" aria-label="Aftershock magnitudes against time since the mainshock on a logarithmic axis">
				{magTicks.map(m => (
					<g key={`m${m}`}>
						<line x1={cChartLeft} x2={cChartWidth - cChartRight} y1={y(m)} y2={y(m)} stroke="rgba(255,255,255,0.08)" />
						<text x={cChartLeft - 4} y={y(m) + 3} textAnchor="end" fontSize={9} fill="rgba(148,163,184,0.9)">M{m}</text>
					</g>
				))}
				{timeTicks.map(t => (
					<g key={`t${t.hours}`}>
						<line x1={x(t.hours)} x2={x(t.hours)} y1={cChartTop} y2={cChartHeight - cChartBottom} stroke="rgba(255,255,255,0.08)" />
						<text x={x(t.hours)} y={cChartHeight - 5} textAnchor="middle" fontSize={9} fill="rgba(148,163,184,0.9)">{t.label}</text>
					</g>
				))}
				{chart.points.map(([hours, mag, depthKm], i) => {
					const future = hours > hoursNow
					return (
						<circle
							key={i}
							cx={x(hours)}
							cy={y(mag)}
							r={1.6 + Math.max(0, mag - chart.magMin) * 0.8}
							fill={depthColor(depthKm / Math.max(chart.depthFloorKm, 1))}
							opacity={future ? 0.12 : 0.9}
						/>
					)
				})}
				{hoursNow >= 0 && (
					<circle cx={cChartLeft} cy={y(chart.mainshockMag)} r={5} fill="white" stroke="rgba(248,113,113,0.9)" strokeWidth={2} />
				)}
				{hoursNow > cMinHours && Math.log10(hoursNow) <= logMax && (
					<line x1={x(hoursNow)} x2={x(hoursNow)} y1={cChartTop} y2={cChartHeight - cChartBottom} stroke="rgba(248,113,113,0.7)" strokeDasharray="3 3" />
				)}
			</svg>
		</div>
	)
}

function alertColor(alert: string): string {
	switch (alert) {
		case "red": return "bg-red-500/20 text-red-300 border border-red-400/30"
		case "orange": return "bg-orange-500/20 text-orange-300 border border-orange-400/30"
		case "yellow": return "bg-yellow-500/20 text-yellow-300 border border-yellow-400/30"
		case "green": return "bg-green-500/20 text-green-300 border border-green-400/30"
		default: return "bg-slate-500/20 text-slate-300 border border-slate-400/30"
	}
}
